import React, { useEffect, useState } from "react";
import { patch, post } from "../lib/api";
import { CHANNELS, FOLLOW_UP_PRESETS, addDays, diffPatch, formatDay } from "../lib/format";
import { todayISO } from "../../helpers/datetime";
import { Button, Checkbox, Chip, Field, Input, Modal, Spinner, Textarea } from "../ui";
import EntityPicker from "../components/EntityPicker";
import { useShell } from "./context";

const TOUCH_KEYS = ["contact_id", "company_id", "touch_date", "channel", "summary", "on_their_mind", "follow_up", "follow_up_date"];

const fromTouch = (t) => ({
    contact: t.contact_id ? { id: t.contact_id, name: t.contact_name || "Contact" } : null,
    company: t.company_id ? { id: t.company_id, name: t.company_name || "Company" } : null,
    channel: t.channel,
    touch_date: t.touch_date,
    summary: t.summary || "",
    on_their_mind: t.on_their_mind || "",
    follow_up: t.follow_up || "",
    follow_up_date: t.follow_up_date || "",
    follow_up_done: !!t.follow_up_done_at,
});

const blank = (prefill = {}) => ({
    contact: prefill.contact || null,
    company: prefill.company || null,
    channel: prefill.channel || "",
    touch_date: prefill.touch_date || todayISO(),
    summary: prefill.summary || "",
    on_their_mind: "",
    follow_up: "",
    follow_up_date: "",
    follow_up_done: false,
});

// "Log a touchpoint": a touch belongs to a contact, a company, or both.
export default function LogTouchModal() {
    const { logState, closeLog, toast, bump, noteWarnings } = useShell();
    const editing = logState && logState.touch;
    // E11 "Log as touch" from a daily-note mention: posted to the mention's
    // route, which logs it on the mentioned record and links it back.
    const mentionId = logState && !editing && logState.prefill && logState.prefill.mentionId;
    const [f, setF] = useState(blank());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!logState) return;
        setF(editing ? fromTouch(logState.touch) : blank(logState.prefill));
        setError(null);
        setBusy(false);
    }, [logState, editing]);

    if (!logState) return null;
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v && v.target ? v.target.value : v }));
    const today = todayISO();
    const suggestCompany =
        !f.company && f.contact && f.contact.current_role && f.contact.current_role.company_id
            ? { id: f.contact.current_role.company_id, name: f.contact.current_role.company_name }
            : null;

    const save = async () => {
        if (!f.contact && !f.company) return setError("Pick a person, a company, or both.");
        if (!f.channel) return setError("Pick a channel.");
        if (f.follow_up_date && !f.follow_up.trim()) return setError("Add what the follow-up is.");
        setError(null);
        setBusy(true);
        const body = {
            contact_id: f.contact ? f.contact.id : null,
            company_id: f.company ? f.company.id : null,
            touch_date: f.touch_date || today,
            channel: f.channel,
            summary: f.summary.trim(),
            on_their_mind: f.on_their_mind.trim(),
            follow_up: f.follow_up.trim(),
            follow_up_date: f.follow_up_date || null,
        };
        try {
            let res;
            if (editing) {
                const orig = logState.touch;
                const changes = diffPatch(orig, body, TOUCH_KEYS);
                if (f.follow_up_done !== !!orig.follow_up_done_at) changes.follow_up_done = f.follow_up_done;
                res = Object.keys(changes).length ? await patch(`v1/crm/touches/${orig.id}`, changes) : orig;
                toast("Touch saved.");
            } else if (mentionId) {
                const clean = { ...body };
                delete clean.contact_id;
                delete clean.company_id;
                Object.keys(clean).forEach((k) => (clean[k] === null || clean[k] === "") && delete clean[k]);
                res = await post(`v1/crm/notes/mentions/${mentionId}/touch`, clean);
                const next = res && res.contact_next_due_date ? ` Next due ${formatDay(res.contact_next_due_date, { short: true })}.` : "";
                toast(`Touch logged from the note.${next}`);
                if (logState.prefill.onLogged) logState.prefill.onLogged(res);
            } else {
                const clean = { ...body };
                Object.keys(clean).forEach((k) => (clean[k] === null || clean[k] === "") && delete clean[k]);
                if (f.contact) {
                    delete clean.contact_id;
                    res = await post(`v1/crm/contacts/${f.contact.id}/touches`, clean);
                } else {
                    delete clean.company_id;
                    res = await post(`v1/crm/companies/${f.company.id}/touches`, clean);
                }
                const next = res && res.contact_next_due_date ? ` Next due ${formatDay(res.contact_next_due_date, { short: true })}.` : "";
                toast(`Touch logged.${next}`);
            }
            noteWarnings(res);
            bump();
            closeLog();
        } catch (e) {
            setError(e.message);
            setBusy(false);
        }
    };

    return (
        <Modal
            open
            title={editing ? "Edit touch" : mentionId ? "Log as touch" : "Log a touchpoint"}
            onClose={closeLog}
            footer={
                <>
                    <Button variant="light" onClick={closeLog}>Cancel</Button>
                    <Button variant="primary" onClick={save} disabled={busy}>
                        {busy && <Spinner />}
                        {editing ? "Save" : "Log touch"}
                    </Button>
                </>
            }
        >
            <div className="hz-form-grid" onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && save()}>
                {error && <div className="hz-alert hz-alert-danger">{error}</div>}
                <div className="hz-form-2">
                    <div>
                        <span className="hz-label">Person</span>
                        <EntityPicker kind="contact" value={f.contact} onChange={set("contact")} autoFocus={!f.contact && !f.company} disabled={!!mentionId} />
                    </div>
                    <div>
                        <span className="hz-label">Company</span>
                        <EntityPicker kind="company" value={f.company} onChange={set("company")} disabled={!!mentionId} />
                        {suggestCompany && !mentionId && (
                            <button type="button" className="hz-btn-link" style={{ marginTop: 4 }} onClick={() => set("company")(suggestCompany)}>
                                <i className="ri-add-line"></i> {suggestCompany.name}
                            </button>
                        )}
                    </div>
                </div>
                <div>
                    <span className="hz-label">Channel</span>
                    <div className="hz-row-wrap" style={{ gap: 6 }}>
                        {CHANNELS.map((c) => (
                            <Chip key={c.value} icon={c.icon} active={f.channel === c.value} onClick={() => set("channel")(c.value)}>
                                {c.label}
                            </Chip>
                        ))}
                    </div>
                </div>
                <div className="hz-form-2" style={{ gridTemplateColumns: "160px 1fr" }}>
                    <Field label="Date">
                        <Input type="date" value={f.touch_date} max={mentionId ? undefined : today} onChange={set("touch_date")} />
                    </Field>
                    <Field label="What happened">
                        <Input value={f.summary} onChange={set("summary")} placeholder="Key points" />
                    </Field>
                </div>
                <Field label="What's on their mind">
                    <Textarea rows={2} value={f.on_their_mind} onChange={set("on_their_mind")} placeholder="Priorities, worries, plans" />
                </Field>
                <div>
                    <span className="hz-label">Follow-up</span>
                    <div className="hz-form-2" style={{ gridTemplateColumns: "1fr 150px" }}>
                        <Input value={f.follow_up} onChange={set("follow_up")} placeholder="What to do next" />
                        <Input type="date" value={f.follow_up_date} onChange={set("follow_up_date")} />
                    </div>
                    <div className="hz-row-wrap" style={{ gap: 6, marginTop: 6 }}>
                        {FOLLOW_UP_PRESETS.map((p) => (
                            <Chip key={p.days} small active={f.follow_up_date === addDays(f.touch_date || today, p.days)} onClick={() => set("follow_up_date")(addDays(f.touch_date || today, p.days))}>
                                {p.label}
                            </Chip>
                        ))}
                        {f.follow_up_date && (
                            <button type="button" className="hz-btn-link" onClick={() => set("follow_up_date")("")}>Clear date</button>
                        )}
                        {editing && (f.follow_up || f.follow_up_date) && (
                            <span style={{ marginLeft: "auto" }}>
                                <Checkbox checked={f.follow_up_done} onChange={set("follow_up_done")}>Follow-up done</Checkbox>
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}
